import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@popperjs/core';
import { ButtonComponent } from '@shared/button/button.component';
import {
  DrawingColors,
  DrawingBrushIconSizes,
  DrawingBrushSizes,
  DrawingTools,
  PaletteColor,
  DrawingBrushOption
} from '@shared/drawing/drawing';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { ControlPopoverComponent } from '../viewer-controls/control-popover/control-popover.component';

@Component({
  selector: 'zbp-viewer-draw',
  imports: [
    ButtonComponent,
    ControlPopoverComponent,
    NgbPopover,
  ],
  templateUrl: './viewer-draw.component.html',
  styleUrl: './viewer-draw.component.scss'
})
export class ViewerDrawComponent implements AfterViewInit {
  @Input() enabled = false;
  @Input() isDesktop: boolean;
  @Output() activeDrawingTool = new EventEmitter<DrawingTools>();
  @Output() clearDrawing = new EventEmitter<any>();
  @Output() activeColor = new EventEmitter<DrawingColors>();
  @Output() activeSize = new EventEmitter<DrawingBrushSizes>();
  activeTool: DrawingTools = DrawingTools.none;
  penIsActive = false;
  eraserIsActive = false;
  tooltipText = '';
  isPopoverOpen = false;

  icon: string = zbpIconNames.pencil2;
  buttonText: string = 'Writing';
  ariaLabel: string = 'Writing Tools';

  colors: PaletteColor[] = [
    { color: DrawingColors.black, name: 'Black' },
    { color: DrawingColors.red, name: 'Red' },
    { color: DrawingColors.darkGreen, name: 'Green' },
    { color: DrawingColors.blue, name: 'Blue' },
    { color: DrawingColors.orange, name: 'Orange' },
    { color: DrawingColors.yellow, name: 'Yellow' },
    { color: DrawingColors.white, name: 'White' },
  ];

  brushSizes: DrawingBrushOption[] = [
    { size: DrawingBrushSizes.xs, name: '1', fontSize: DrawingBrushIconSizes.xs},
    { size: DrawingBrushSizes.sm, name: '2', fontSize: DrawingBrushIconSizes.sm},
    { size: DrawingBrushSizes.med, name: '3', fontSize: DrawingBrushIconSizes.med},
    { size: DrawingBrushSizes.lg, name: '4', fontSize: DrawingBrushIconSizes.lg},
    { size: DrawingBrushSizes.xl, name: '5', fontSize: DrawingBrushIconSizes.xl},
    { size: DrawingBrushSizes.xxl, name: '6', fontSize: DrawingBrushIconSizes.xxl},
  ];

  currentColor: PaletteColor = this.colors[0];

  currentSize: DrawingBrushOption = this.brushSizes[2];

  protected readonly zbpIconNames = zbpIconNames;

  @ViewChild('popoverControl') popoverControl!: ControlPopoverComponent;

  constructor(private cdRef: ChangeDetectorRef) {
  }

  ngAfterViewInit(): void {
    if (this.enabled) {
      this.tooltipText = '';
    } else {
      this.tooltipText = 'Drawing tools are not available for this item.';
    }

    this.cdRef.detectChanges();
  }

  setActiveTool(toolName: string): void {
    if (this.activeTool === DrawingTools[toolName]) {
      this.activeTool = DrawingTools.none;
    } else {
      this.activeTool = DrawingTools[toolName];
    }

    this.penIsActive = this.activeTool === DrawingTools.draw;
    this.eraserIsActive = this.activeTool === DrawingTools.erase;

    this.setIconAndButtonText();

    this.activeDrawingTool.emit(this.activeTool);
  }

  setIconAndButtonText(): void {
    if (this.eraserIsActive) {
      this.icon = zbpIconNames.eraserIcon2;
      this.buttonText = 'Eraser';
      this.ariaLabel = 'Eraser Active';
    } else if (this.penIsActive) {
      this.icon = zbpIconNames.pencil2;
      this.buttonText = `Pen (${this.currentColor.name})`;
      this.ariaLabel = `${this.currentColor.name} Pen Active`;
    } else {
      this.icon = zbpIconNames.pencil2;
      this.buttonText = 'Writing';
      this.ariaLabel = 'Writing Tools';
    }
  }

  selectColor(color: DrawingColors) {
    this.currentColor = this.colors.find(option => option.color === color);
    this.activeColor.emit(color);

    if (!this.activeTool || this.activeTool !== DrawingTools.draw) {
      this.setActiveTool(DrawingTools.draw);
    }

    this.setIconAndButtonText();
  }

  selectSize(size: DrawingBrushOption) {
    this.activeSize.emit(size.size);
    this.currentSize = size;
  }

  /**
   * Sets the background color based on tool selected (black if eraser or not black color)
   * For black, return a lighter shade of gray
   */
  getBrushBackgroundColor(): string {
    if (this.activeTool && this.activeTool === DrawingTools.erase || this.currentColor?.color !== DrawingColors.black) {
      return DrawingColors.black;
    }

    // Black works with this gray
    return '#858585';
  }

  clear() {
    this.clearDrawing.emit(true);
  }

  showPopover() {
    this.isPopoverOpen = true;
    this.popoverControl.onPopoverShown();
  }

  closePopover() {
    this.isPopoverOpen = false;
  }

  // Modifying Popper.js options
  popperOptions(options: Options): Options {
    return {
      ...options,
      strategy: 'absolute',
      modifiers: [
        ...(options.modifiers || []),
        {
          name: 'offset',
          options: {
            offset: [0, 0],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            // Allows popover to stay visible even when scrolled
            padding: 0,
          },
        },
      ],
    };
  }

  protected readonly DrawingColors = DrawingColors;
  protected readonly DrawingTools = DrawingTools;
}
